import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import WpDynamicTable from "../../Components/WhatsappTable/WpDynamicTable";
import {
  AddGeoResolverRegistration,
  DeleteGeoResolverRegistration,
  fetchGeoResolverMain,
  fetchGeoResolverRegistration,
  UpdateGeoResolverRegistration,
} from "../../Redux/WhatsappSlice/GeoResolver";
import { fetchAllClients } from "../../Redux/WhatsappSlice/Client";
import Loader from "../../Components/Loader/Loader";

const GeoResolverRegistration = () => {
  const dispatch = useDispatch();
  const { GeoResolverRegistrationData, GeoResolverRegistrationLoading, GeoResolverMainData } = useSelector(
    (state) => state.GeoResolver
  );

  // Client list from redux
  const { AllClients } = useSelector((state) => state.Client);

  const getCurrentDateTime = () => {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    const hh = String(now.getHours()).padStart(2, '0');
    const min = String(now.getMinutes()).padStart(2, '0');
    // Return format suitable for input datetime-local (YYYY-MM-DDTHH:mm)
    return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
  };

  const [formData, setFormData] = useState({
    Provider: "",
    UserDate: getCurrentDateTime(),
  });

  const [selectedClientId, setSelectedClientId] = useState("");
  const [editId, setEditId] = useState(null);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    dispatch(fetchGeoResolverRegistration());
    dispatch(fetchGeoResolverMain());
    dispatch(fetchAllClients());
  }, [dispatch]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    const errs = {};
    if (!selectedClientId) errs.ClientID = "Please select a Client";
    if (!formData.Provider) errs.Provider = "Please select a Provider";
    if (!formData.UserDate) errs.UserDate = "Please select a Date & Time";
    return errs;
  };

  const resetForm = () => {
    setFormData({ Provider: "", UserDate: getCurrentDateTime() });
    setSelectedClientId("");
    setEditId(null);
    setErrors({});
  };

  const handleEdit = (row) => {
    setEditId(row.RegId);
    setSelectedClientId(row.ClientId || "");

    let inputValue = "";
    if (row.UserDate && row.UserDate !== "-") {
      const parts = row.UserDate.split(' ');
      const datePart = parts[0] || "";
      const timePart = parts[1] ? parts[1].slice(0, 5) : ""; // HH:mm
      if (datePart && timePart) {
        inputValue = `${datePart}T${timePart}`;
      }
    }

    setFormData({
      Provider: row.ProviderId || "",
      UserDate: inputValue || getCurrentDateTime(),
    });
  };

  const handleDelete = async (row) => {
    if (!window.confirm("Are you sure you want to delete this registration?")) return;
    try {
      await dispatch(DeleteGeoResolverRegistration({ id: row.RegId })).unwrap();
      toast.success("Registration deleted successfully!", { position: "top-right", autoClose: 1000 });
      dispatch(fetchGeoResolverRegistration());
    } catch (error) {
      toast.error(`Failed to delete: ${error}`, { position: "top-right", autoClose: 1500 });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      // Convert datetime-local value to backend format "YYYY-MM-DD HH:mm:ss"
      const formattedDate = formData.UserDate ? `${formData.UserDate.replace('T', ' ')}:00` : "";
      const payload = {
        Provider: formData.Provider,
        UserDate: formattedDate,
        ClientID: selectedClientId,
      };

      if (editId) {
        await dispatch(UpdateGeoResolverRegistration({ id: editId, formData: payload })).unwrap();
        toast.success("Registration updated successfully!", { position: "top-right", autoClose: 1000 });
      } else {
        await dispatch(AddGeoResolverRegistration({ clientId: selectedClientId, values: payload })).unwrap();
        toast.success("Registration added successfully!", { position: "top-right", autoClose: 1000 });
      }
      resetForm();
      dispatch(fetchGeoResolverRegistration());
    } catch (error) {
      toast.error(`Failed to ${editId ? "update" : "add"}: ${error?.message || "Unknown error"}`, {
        position: "top-right",
        autoClose: 1500,
      });
    }
  };

  const columns = [
    { field: "id", headerName: "No", flex: 0.5 },
    { field: "ClientName", headerName: "Client", flex: 1.5 },
    { field: "ProviderName", headerName: "Provider", flex: 1 },
    { field: "UserDate", headerName: "Date", flex: 1 },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      renderCell: (params) => (
        <div className="flex space-x-2 justify-center">
          <button
            className="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600"
            onClick={() => handleEdit(params.row)}
          >
            Update
          </button>
          <button
            className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600"
            onClick={() => handleDelete(params.row)}
          >
            Delete
          </button>
        </div>
      ),
    },
  ];

  const rows = (GeoResolverRegistrationData || []).map((el, index) => ({
    id: index + 1,
    RegId: el._id,
    ClientName: el?.ClientID?.WhaClientName || "-",
    ClientId: el?.ClientID?._id || "",
    ProviderName: el?.Provider?.Provider || el?.Provider?.Base_Url || "-",
    ProviderId: el?.Provider?._id || "",
    UserDate: el?.UserDate || "-",
  }));

  const sortedRows = [...rows]
    .sort((a, b) => (a.ClientName || "").localeCompare(b.ClientName || "", undefined, { sensitivity: 'base' }))
    .map((item, idx) => ({ ...item, id: idx + 1 }));

  const sortedClients = AllClients?.data
    ? [...AllClients.data].sort((a, b) =>
        (a.WhaClientName || "").localeCompare(b.WhaClientName || "", undefined, { sensitivity: 'base' })
      )
    : [];

  const sortedProviders = GeoResolverMainData
    ? [...GeoResolverMainData].sort((a, b) =>
        (a.Provider || a.Base_Url || "").localeCompare(b.Provider || b.Base_Url || "", undefined, { sensitivity: 'base' })
      )
    : [];

  if (GeoResolverRegistrationLoading) return <Loader />;

  return (
    <>
      <div>
        <div className="title">
          <h3 className="text-xl font-bold main_title_page text-white px-4 py-2 inline-block rounded">
            {editId ? "Update GeoResolver Registration" : "Add GeoResolver Registration"}
          </h3>
        </div>

        <div className="Form_bg_color bg-white shadow-sm rounded-xl mt-4">
          <form
            onSubmit={handleSubmit}
            className="grid grid-cols-12 gap-4 p-3 rounded-2xl"
          >
            {/* Client Dropdown (always visible) */}
            <div className="col-span-12 lg:col-span-4 md:col-span-6">
              <h5 className="mb-1 block font-semibold">Select Client</h5>
              <select
                value={selectedClientId}
                onChange={(e) => setSelectedClientId(e.target.value)}
                className="bg-[#F3F8FB] w-full p-2 border-2 border-black rounded"
              >
                <option value="">-- Select Client --</option>
                {sortedClients.map((item) => (
                  <option key={item._id} value={item._id}>
                    {item.WhaClientName} ({item.StaticIP})
                  </option>
                ))}
              </select>
              {errors.ClientID && <p className="text-red-500 text-sm mt-1">{errors.ClientID}</p>}
            </div>

            {/* Provider Dropdown */}
            <div className="col-span-12 lg:col-span-4 md:col-span-6">
              <h5 className="mb-1 block font-semibold">Select Provider</h5>
              <select
                name="Provider"
                value={formData.Provider}
                onChange={handleChange}
                className="bg-[#F3F8FB] w-full p-2 border-2 border-black rounded"
              >
                <option value="">-- Select Provider --</option>
                {sortedProviders.map((item) => (
                  <option key={item._id} value={item._id}>
                    {item.Provider || item.Base_Url}
                  </option>
                ))}
              </select>
              {errors.Provider && <p className="text-red-500 text-sm mt-1">{errors.Provider}</p>}
            </div>
            {/* Date Input */}
            <div className="col-span-12 lg:col-span-4 md:col-span-6">
              <h5 className="mb-1 block font-semibold">Date & Time</h5>
              <input
                type="datetime-local"
                name="UserDate"
                value={formData.UserDate}
                onChange={handleChange}
                className="bg-[#F3F8FB] w-full p-2 border-2 border-black rounded"
              />
              {errors.UserDate && <p className="text-red-500 text-sm mt-1">{errors.UserDate}</p>}
            </div>

            {/* Submit */}
            <div className="submit_Form col-span-12 flex justify-center">
              <button type="submit" className="All_button">
                {editId ? "Update" : "Submit"}
              </button>
              {editId && (
                <button
                  type="button"
                  className="bg-gray-500 text-white ms-2 px-4 py-2 rounded hover:bg-gray-600"
                  onClick={resetForm}
                >
                  Cancel
                </button>
              )}
            </div>
          </form>
        </div>

        <div className="px-2 py-6">
          <div className="title">
            <h3 className="text-xl font-bold main_title_page text-white px-4 py-2 inline-block rounded">
              View GeoResolver Registrations
            </h3>
          </div>
          <div className="rounded-lg overflow-x-auto mt-3">
            <WpDynamicTable columns={columns} rows={sortedRows} height="550px" />
          </div>
        </div>
      </div>
    </>
  );
};

export default GeoResolverRegistration;
